import { CampoAlteracao, DadosPendenciaConcluida } from "./types";
import { mapearAnexos } from "../../Compartilhado/utils";
import type { Email } from "../DemandaCriada/types";
import { formatFileSize } from "../../../../../../../utility/byteSizeUtils";
import { formatCurrency } from "../../../../../../../utility/currencyUtils";
import { parseJsonSafely } from "../../../../../../../utility/jsonUtils";
import { extractFilename } from "../../../../../../../utility/filePathUtils";
import { mapearEmail } from "../../../../../../../utility/historicoLogUtils";
import { getStatusLabelStatusKanban, getResultadoLabelPendencia } from "../../../../../../../utility/statusPendenciaKanban";

const formatDate = (value: string | Date | null | undefined): string => {
    if (!value) return '—';
    try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return '—';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch {
        return '—';
    }
};

export const mapearCamposUnificados = (logPendencia: any, dadosExtra: any): CampoAlteracao[] => {
    if (!logPendencia || !logPendencia.dados_depois) {
        return [];
    }

    const dadosAntes = parseJsonSafely(logPendencia.dados_antes);
    const dadosDepois = parseJsonSafely(logPendencia.dados_depois);
    const extra = parseJsonSafely(dadosExtra);
    
    const campos: CampoAlteracao[] = [];

    if (dadosDepois.resultado !== undefined) {
        const resultadoDepois = dadosDepois.resultado;
        const resultadoAntes = dadosAntes?.resultado || null;
        if (resultadoAntes !== resultadoDepois) {
            campos.push({
                campo: 'Resultado',
                antes: resultadoAntes ? getResultadoLabelPendencia(resultadoAntes) : '—',
                depois: getResultadoLabelPendencia(resultadoDepois)
            });
        }
    }


    if (dadosAntes?.valor !== undefined || dadosDepois?.valor !== undefined) {
        const valorAntes = dadosAntes?.valor;
        const valorDepois = extra?.valor || dadosDepois?.valor;
        if (valorAntes !== valorDepois) {
            campos.push({
                campo: 'Valor Solicitado',
                antes: valorAntes !== undefined ? formatCurrency(valorAntes) : '—',
                depois: valorDepois !== undefined ? formatCurrency(valorDepois) : '—'
            });
        }
    }

    const tipoPendencia = dadosDepois.tipo || dadosAntes?.tipo;
    
    if (dadosDepois.resposta !== undefined) {
        const respostaAntes = dadosAntes?.resposta || null;
        const respostaDepois = dadosDepois.resposta;
        
        if (respostaAntes !== respostaDepois) {
            if (tipoPendencia === 'documentacao') {
                campos.push({
                    campo: 'Informações sobre a documentação',
                    antes: respostaAntes || '—',
                    depois: respostaDepois || '—'
                });
            } else if (tipoPendencia === 'informacao' || tipoPendencia === 'orientacao') {
                campos.push({
                    campo: 'Resposta',
                    antes: respostaAntes || '—',
                    depois: respostaDepois || '—'
                });
            } else {
                campos.push({
                    campo: 'Resposta',
                    antes: respostaAntes || '—',
                    depois: respostaDepois || '—'
                });
            }
        }
    }

    if ((dadosDepois.tipo === 'marjoracao' || dadosDepois.tipo === 'reembolso' || dadosDepois.tipo === 'adiantamento') && extra?.justificativa) {
        campos.push({
            campo: 'Justificativa',
            antes: '—',
            depois: extra.justificativa
        });
    }

    if (dadosAntes?.status_kanban && dadosDepois?.status_kanban) {
        campos.push({
            campo: 'Status',
            antes: getStatusLabelStatusKanban(dadosAntes.status_kanban),
            depois: getStatusLabelStatusKanban(dadosDepois.status_kanban)
        });
    }

    return campos;
};

export const processarDadosPendenciaConcluida = (logs: any[]): DadosPendenciaConcluida => {
    const logPendencia = logs.find((log: any) => log.entidade_nome === 'pendencia');
    
    if (!logPendencia) {
        return {
            campos: [],
            anexos: [],
            email: null,
            idPendencia: undefined
        };
    }

    const dadosExtra = parseJsonSafely(logPendencia.dados_extra);
    const campos = mapearCamposUnificados(logPendencia, dadosExtra);
    let anexos = mapearAnexos(logs);
    
    if (dadosExtra && dadosExtra.arquivos && Array.isArray(dadosExtra.arquivos)) {
        const anexosIdsMapeados = new Set(anexos.map(a => a.id).filter(Boolean));
        const logsArquivosMap = new Map();
        
        for (const log of logs) {
            if ((log.entidade_nome === 'proposta_arquivo' || log.entidade_nome === 'orientacao_arquivo') && log.entidade_id) {
                const logDadosExtra = parseJsonSafely(log.dados_extra);
                logsArquivosMap.set(log.entidade_id, logDadosExtra);
            }
        }
        
        for (const arquivo of dadosExtra.arquivos) {
            if (arquivo.id_proposta_arquivo && !anexosIdsMapeados.has(arquivo.id_proposta_arquivo)) {
                const caminho = arquivo.caminho;
                const filename = extractFilename(caminho);
                const logArquivo = logsArquivosMap.get(arquivo.id_proposta_arquivo);
                const tamanhoArquivo = arquivo.tamanho || logArquivo?.tamanho;
                const size = formatFileSize(tamanhoArquivo);
                const excluidoAtual = arquivo.excluido === true || arquivo.excluido === 1;
                
                if (caminho && filename && filename !== '—') {
                    anexos.push({
                        id: arquivo.id_proposta_arquivo,
                        filename,
                        size: size !== '—' ? size : '—',
                        excluido: excluidoAtual
                    });
                }
            }
        }
    }
    
    const email = mapearEmail(logs);

    return {
        campos,
        anexos,
        email,
        idPendencia: logPendencia.entidade_id
    };
};

